import { useFlyOutProductsBasedOnSelectedCloud } from '../../common/utils/hooks/useFlyOutProductsBasedOnSelectedCloud'
import { useEffect, useMemo, ReactNode } from 'react'
import { useParams, useLocation } from 'react-router-dom'
import { useDispatch, useSelector } from '../../store'
import {
  getCurrentPrivateOffer,
  getPrivateOffers,
  setPageNumber,
  setPageSize,
} from '../../modules/privateOffer/action'
import { getCurrentPrivateOfferInCreation } from '../../modules/privateOffer/privateOfferCreation/action'
import { PrivateOffersState } from '../../modules/privateOffer/types'
import {
  LoadingTypes,
  isLoading,
  useCurrentCloudProvider,
  useScreenSize,
} from '@labrav/common'
import { isEmpty } from 'lodash'
import { makeStyles } from 'tss-react/mui'
import { Product } from '../../modules/productsListing/types'

export const useStyles = makeStyles()(theme => ({
  content: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    backgroundColor: theme.palette.opaque.light,
  },
}))

const defaultPageNumber = 1

type PrivateOffersWrapperProps = {
  secureKeyProp?: string
  partnerIdProp?: string
  privateOfferIdProp?: string
  productIdProp?: string
  children:
    | ReactNode
    | React.FC<{
        externalBaseURL?: string
        search?: string
        crmOpportunityId?: string
        crmObjectLink?: string
        selectedProductBasedOnCloud?: Product | undefined
      }>
}

const PrivateOffersWrapper: React.FC<PrivateOffersWrapperProps> = ({
  children,
  secureKeyProp,
  partnerIdProp,
  privateOfferIdProp,
  productIdProp,
}: PrivateOffersWrapperProps) => {
  const { search, pathname } = useLocation()
  const { classes } = useStyles()
  const dispatch = useDispatch()
  const {
    secureKey: secureKeyParam,
    partnerId: partnerIdParam,
    privateOfferId: privateOfferIdParam,
    productId: productIdParam,
    cloud: routeCloud,
    accountId: routeAccountId,
  } = useParams<{
    secureKey: string
    partnerId: string
    privateOfferId?: string
    productId?: string
    cloud?: string
    accountId?: string
  }>()
  const { pageSize: pageSizeValue } = useScreenSize()
  const pageSizeTobeSent = pageSizeValue

  const secureKey = secureKeyParam || secureKeyProp
  const partnerId = partnerIdParam || partnerIdProp
  const privateOfferId = privateOfferIdParam || privateOfferIdProp
  const productId = productIdParam || productIdProp
  const selectedCloudMarketplace = useCurrentCloudProvider()
  const loading = useSelector<boolean>(state =>
    isLoading(state.loading)(LoadingTypes.GENERAL)
  )
  const { privateOffers } = useSelector<PrivateOffersState>(
    state => state.privateOffers
  )
  const crmOpportunityId = new URLSearchParams(search)
    .get('hs_object_id')
    ?.toLowerCase()

  const { products, selectedProduct: selectedProductFromFilter } =
    useFlyOutProductsBasedOnSelectedCloud()
  const selectedProductBasedOnCloud = products.find(
    productItem => productItem.productId === productId
  )
  const createPrivateOfferModalOpen = useSelector<boolean>(
    state => state.productListingFilters?.createPrivateOfferModalOpen ?? false
  )

  const crmObjectLink = new URLSearchParams(search).get('object_link') || ''
  const { isPOPage, isNewPOPage, isEditPOPage, isPODetailsPage } = useMemo(
    () => ({
      isPOPage:
        (pathname.includes('private-offers') ||
          pathname.includes('resale-authorizations')) &&
        !privateOfferId,
      isNewPOPage: pathname.includes('private-offers/edit') && !privateOfferId,
      isEditPOPage:
        pathname.includes('private-offers') &&
        pathname.includes('edit') &&
        privateOfferId,
      isPODetailsPage:
        pathname.includes('private-offers') &&
        pathname.includes('details') &&
        privateOfferId,
    }),
    [pathname, privateOfferId]
  )
  const externalBaseURL = useMemo(() => {
    const base =
      routeCloud && routeAccountId
        ? `/external-apps/hubspot/v1/${secureKey}/partners/${partnerId}/cloud/${routeCloud}/accounts/${routeAccountId}/product-listing`
        : `/external-apps/hubspot/v1/${secureKey}/partners/${partnerId}/product-listing`
    return `${base}${productId ? `/${productId}` : ''}`
  }, [secureKey, partnerId, productId, routeCloud, routeAccountId])
  const fetchPageData = async () => {
    if (partnerId && secureKey && routeCloud && routeAccountId) {
      if (
        isPOPage &&
        selectedProductFromFilter !== undefined &&
        !isEmpty(products)
      ) {
        await dispatch(
          getPrivateOffers({
            partnerId: routeAccountId,
            cloudMarketplace: selectedCloudMarketplace,
            productId: selectedProductFromFilter,
            crmOpportunityId,
            pageSize: pageSizeTobeSent,
          })
        )
      } else if ((isNewPOPage || isEditPOPage) && productId) {
        await dispatch(
          getCurrentPrivateOfferInCreation({
            productId: productId || '',
            partnerId: routeAccountId,
            privateOfferId: privateOfferId || '',
            crmOpportunityId,
          })
        )
      } else if (
        isPODetailsPage &&
        privateOfferId &&
        !isEmpty(selectedProductBasedOnCloud)
      ) {
        await dispatch(
          getCurrentPrivateOffer(
            routeAccountId,
            privateOfferId,
            selectedCloudMarketplace,
            productId,
            crmOpportunityId
          )
        )
      }

      dispatch(setPageNumber(defaultPageNumber))
      dispatch(setPageSize(pageSizeTobeSent))
    }
  }

  useEffect(() => {
    fetchPageData()
  }, [
    secureKey,
    partnerId,
    privateOfferId,
    routeAccountId,
    routeCloud,
    selectedProductFromFilter,
    selectedProductBasedOnCloud,
  ])

  if (
    !createPrivateOfferModalOpen &&
    loading &&
    isPOPage &&
    isEmpty(privateOffers) &&
    !(isNewPOPage || isEditPOPage)
  ) {
    return <div data-testid="private-offers-loader" />
  }

  return (
    <div data-testid="private-offers-wrapper">
      {typeof children === 'function'
        ? children({
            externalBaseURL,
            search,
            crmOpportunityId,
            crmObjectLink,
          })
        : children}
    </div>
  )
}

export default PrivateOffersWrapper
